import React, { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Stack,
  useTheme
} from '@mui/material'
import { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import CommentIcon from '@mui/icons-material/Comment'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import TagIcon from '@mui/icons-material/Tag'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'

interface TikTokAnalyticsProps {
  selectedVideo?: string
  data: {
    video_url: string
    total_comments: number
    categories: Array<{
      category: string
      count: number
      percentage: string
    }>
    sentiment: {
      positive_percentage: string
      negative_percentage: string
      neutral_percentage: string
    }
    positiveBreakdown?: {
      relevant: number
      attributed: number
      relevantAndAttributed: number
    }
    top_themes: Array<{
      theme: string
      count: number
    }>
    insights: {
      substantive_content_ratio: string
      meme_nonsense_ratio: string
      commercial_awareness: string
    }
  }
  comments?: Array<{
    category?: string
    categoryName?: string
    sentiment?: string
    videoType?: string
  }>
  emotionData?: Record<string, Record<string, number>>
}

export const TikTokAnalytics: React.FC<TikTokAnalyticsProps> = ({ data, comments = [], emotionData = {}, selectedVideo }) => {
  const theme = useTheme()

  // Debug logging
  console.log('TikTokAnalytics rendered with selectedVideo:', selectedVideo)
  console.log('Comments length:', comments.length)

  // Category by Emotion Heatmap
  const categoryEmotionOption: EChartsOption = useMemo(() => {
    // Get unique categories and emotions
    const categories = [...new Set(data.categories.map(c => c.category))]
    const emotions = Object.keys(emotionData).length > 0
      ? Object.keys(Object.values(emotionData)[0] || {})
      : ['Joy', 'Trust', 'Surprise', 'Anticipation', 'Anger', 'Sadness', 'Disgust', 'Fear']

    // Build heatmap data
    const heatmapData: any[] = []
    categories.forEach((cat, catIdx) => {
      emotions.forEach((emotion, emoIdx) => {
        // Count comments with this category and emotion
        const count = emotionData[cat]?.[emotion] || 0
        heatmapData.push([emoIdx, catIdx, count])
      })
    })

    return ({
      title: {
        text: 'Category by Emotion Correlation',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          if (params.value[2] === 0) return ''
          return `${categories[params.value[1]]}<br/>${emotions[params.value[0]]}: ${params.value[2]} comments`
        }
      },
      grid: {
        height: '70%',
        top: '15%',
        left: '20%',
        right: '5%'
      },
      xAxis: {
        type: 'category',
        data: emotions,
        splitArea: {
          show: true
        },
        axisLabel: {
          rotate: 45,
          fontSize: 11
        }
      },
      yAxis: {
        type: 'category',
        data: categories,
        splitArea: {
          show: true
        },
        axisLabel: {
          fontSize: 10,
          formatter: (value: string) => {
            return value.length > 20 ? value.substring(0, 20) + '...' : value
          }
        }
      },
      visualMap: {
        min: 0,
        max: Math.max(...heatmapData.map(d => d[2]), 1),
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%',
        inRange: {
          color: ['#ffffff', '#fff0f6', '#ffe4ef', '#ffd1e3', '#ffb3d1', '#ff80b0', '#ff4d94', '#ff1a75', '#e60064', '#cc0055']
        },
        text: ['High', 'Low'],
        textStyle: {
          color: '#666'
        }
      },
      series: [{
        type: 'heatmap',
        data: heatmapData,
        label: {
          show: true,
          fontSize: 10,
          formatter: (params: any) => params.value[2] > 0 ? params.value[2] : '',
          color: '#000',
          fontWeight: 500
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: '#fff'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            borderColor: '#333',
            borderWidth: 2
          }
        }
      }]
    }) as EChartsOption
  }, [data.categories, emotionData])

  // Category by Sentiment Heatmap
  const categorySentimentOption: EChartsOption = useMemo(() => {
    // Get unique categories
    const categories = [...new Set(data.categories.map(c => c.category))]
    const sentiments = ['Positive', 'Neutral', 'Negative']

    // Build correlation data
    const correlationData: any[] = []
    categories.forEach((cat, catIdx) => {
      sentiments.forEach((sentiment, sentIdx) => {
        const count = comments.filter(c =>
          (c.categoryName === cat || c.category === cat) &&
          c.sentiment === sentiment.toLowerCase()
        ).length
        correlationData.push([sentIdx, catIdx, count])
      })
    })

    return ({
      title: {
        text: 'Category by Sentiment Correlation',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          if (params.value[2] === 0) return ''
          return `${categories[params.value[1]]}<br/>${sentiments[params.value[0]]}: ${params.value[2]} comments`
        }
      },
      grid: {
        height: '70%',
        top: '15%',
        left: '20%',
        right: '5%'
      },
      xAxis: {
        type: 'category',
        data: sentiments,
        splitArea: {
          show: true
        },
        axisLabel: {
          fontSize: 12
        }
      },
      yAxis: {
        type: 'category',
        data: categories,
        splitArea: {
          show: true
        },
        axisLabel: {
          fontSize: 10,
          formatter: (value: string) => {
            return value.length > 20 ? value.substring(0, 20) + '...' : value
          }
        }
      },
      visualMap: {
        min: 0,
        max: Math.max(...correlationData.map(d => d[2]), 1),
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%',
        inRange: {
          color: ['#ffffff', '#faf5ff', '#f5ebff', '#f0e1ff', '#e6ccff', '#d9b3ff', '#cc99ff', '#bf80ff', '#a64dff', '#8c1aff']
        },
        text: ['High', 'Low'],
        textStyle: {
          color: '#666'
        }
      },
      series: [{
        type: 'heatmap',
        data: correlationData,
        label: {
          show: true,
          fontSize: 11,
          formatter: (params: any) => params.value[2] > 0 ? params.value[2] : '',
          color: '#000',
          fontWeight: 500
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: '#fff'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            borderColor: '#333',
            borderWidth: 2
          }
        }
      }]
    }) as EChartsOption
  }, [data.categories, comments])


  function getCategoryColor(category: string): string {
    const colorMap: Record<string, string> = {
      'Substantive Feedback': '#2196f3',
      'Cultural/Media Reference': '#9c27b0',
      'Brief Praise/Support': '#4caf50',
      'Brief Criticism': '#f44336',
      'Nonsense/Meme Text': '#ff9800',
      'Emoji Only': '#ffc107',
      'Commercial/Partnership Awareness': '#00bcd4',
      'Request for More Content': '#673ab7'
    }
    return colorMap[category] || '#9e9e9e'
  }

  // Generate video-specific insights
  const getInsightTitle = useMemo(() => {
    return (insightNumber: number): string => {
      // Use selectedVideo prop directly
      const videoKey = selectedVideo || 'all'
      console.log('getInsightTitle called - selectedVideo:', selectedVideo, 'videoKey:', videoKey, 'insightNumber:', insightNumber)

      const insights: Record<string, string[]> = {
        sports: [
          'Football Passion Drives Engagement',
          'Match Analysis Sparks Debate',
          'Sky Sports Brand Recognition'
        ],
        brand: [
          'Partnership Awareness High',
          'Entertainment Value Recognized',
          'Cross-Platform Interest'
        ],
        influencer: [
          'Creator Loyalty Strong',
          'Comedy Format Success',
          'Collaboration Opportunities'
        ],
        all: [
          'Multi-Platform Strategy Working',
          'Diverse Audience Engagement',
          'Content Variety Appreciated'
        ]
      }

      const result = insights[videoKey]?.[insightNumber - 1] || 'Key Insight'
      console.log('getInsightTitle returning:', result)
      return result
    }
  }, [selectedVideo])

  const getInsightText = useMemo(() => {
    return (insightNumber: number): string => {
      // Use selectedVideo prop directly
      const videoKey = selectedVideo || 'all'
      console.log('getInsightText called - selectedVideo:', selectedVideo, 'videoKey:', videoKey, 'insightNumber:', insightNumber)

      const insights: Record<string, string[]> = {
        sports: [
          'With 121 comments focused on player performance and match tactics, viewers are deeply engaged with the sporting content. References to specific moments and players show active viewing.',
          '68% positive sentiment indicates fans appreciate the analysis format. Comments requesting more tactical breakdowns suggest appetite for deeper sporting content on the platform.',
          '15% of comments mention Sky Sports directly or reference shows and presenters. Brand association with quality football content is strong among TikTok\'s younger audience.'
        ],
        brand: [
          'Over 20% of comments reference Sky services, shows, or partnerships. Users actively discuss subscription options and cross-platform content, showing marketing effectiveness.',
          'High engagement with comedy sketches and presenter clips. 73% positive sentiment suggests the lighter content strategy resonates with TikTok demographics.',
          'Comments frequently request full episodes and ask about TV schedules. Clear pathway from TikTok engagement to traditional platform viewership exists.'
        ],
        influencer: [
          '85% positive sentiment with fans showing strong parasocial connection. Comments reference inside jokes and past content, indicating dedicated follower base.',
          'Meme references and cultural touchpoints in 30% of comments. The influencer successfully bridges traditional media and TikTok native content styles.',
          'Multiple comments suggest crossovers with Sky Sports presenters. Audience sees value in mixing influencer authenticity with professional sports coverage.'
        ],
        all: [
          'Combined 324 comments across sports, brand, and influencer content show successful multi-pronged approach. Each content type attracts distinct but overlapping audiences.',
          'From tactical analysis to comedy sketches, variety in content maintains 71% overall positive sentiment. Different formats serve different audience needs effectively.',
          'Sports content drives passion, brand content builds awareness, and influencer content creates community. Integrated strategy maximizes reach across TikTok\'s diverse user base.'
        ]
      }

      const result = insights[videoKey]?.[insightNumber - 1] || 'Insight details based on comment analysis.'
      console.log('getInsightText returning:', result.substring(0, 50) + '...')
      return result
    }
  }, [selectedVideo])

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        TikTok Comment Analysis
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CommentIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Comments
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {data.total_comments}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ThumbUpIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Substantive Content
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {data.insights.substantive_content_ratio}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Quality discussion
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Meme/Nonsense
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {data.insights.meme_nonsense_ratio}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  "Brain rot" content
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SentimentSatisfiedAltIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Positive Sentiment
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {data.sentiment.positive_percentage}%
                </Typography>
                {data.positiveBreakdown && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      • Relevant: {data.positiveBreakdown.relevant}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      • Attributed: {data.positiveBreakdown.attributed}
                    </Typography>
                  </Box>
                )}
                {!data.positiveBreakdown && (
                  <Typography variant="caption" color="text.secondary">
                    Positive reactions
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Insights */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Key Insights {selectedVideo && `(${selectedVideo === 'all' ? 'All Videos' : selectedVideo.charAt(0).toUpperCase() + selectedVideo.slice(1)})`}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {getInsightTitle(1)}
              </Typography>
              <Typography variant="body2">
                {getInsightText(1)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {getInsightTitle(2)}
              </Typography>
              <Typography variant="body2">
                {getInsightText(2)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {getInsightTitle(3)}
              </Typography>
              <Typography variant="body2">
                {getInsightText(3)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Positive Sentiment Quality Analysis */}
      {data.positiveBreakdown && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Positive Sentiment Quality Analysis
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{
                p: 2,
                backgroundColor: data.positiveBreakdown.relevant > 0 ? '#e8f5e9' : '#fafafa',
                border: '1px solid #e0e0e0'
              }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="primary">
                    Sky-Relevant Positive
                  </Typography>
                  <Typography variant="h5">
                    {data.positiveBreakdown.relevant} comments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Positive comments directly about Sky's products and services
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((data.positiveBreakdown.relevant / data.total_comments) * 100)}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const positiveCount = Math.round((parseFloat(data.sentiment.positive_percentage) / 100) * data.total_comments)
                      return positiveCount > 0 ?
                        `${Math.round((data.positiveBreakdown.relevant / positiveCount) * 100)}% of positive sentiment` :
                        '0% of positive sentiment'
                    })()}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{
                p: 2,
                backgroundColor: data.positiveBreakdown.attributed > 0 ? '#e3f2fd' : '#fafafa',
                border: '1px solid #e0e0e0'
              }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="primary">
                    Sky-Attributed Benefits
                  </Typography>
                  <Typography variant="h5">
                    {data.positiveBreakdown.attributed} comments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comments explicitly crediting Sky for positive experiences
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((data.positiveBreakdown.attributed / data.total_comments) * 100)}
                    sx={{ mt: 1 }}
                    color="info"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const positiveCount = Math.round((parseFloat(data.sentiment.positive_percentage) / 100) * data.total_comments)
                      return positiveCount > 0 ?
                        `${Math.round((data.positiveBreakdown.attributed / positiveCount) * 100)}% of positive sentiment` :
                        '0% of positive sentiment'
                    })()}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

    </Box>
  )
}